import React, {
  useState,
  useMemo,
} from 'react';
import { useQuery, useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import _ from 'lodash';

import {
  ErrorHandler, ConfirmDialog, Loader, captureException, Toggle,
} from '@playbookux/playbook-web-core';
import {
  WRITE_FEEDBACK,
  RESET_RATING_AND_REVIEW,
  LAST_EDITION,
  LOCK_PROFILE_EDITION,
  UNLOCK_PROFILE_EDITION,
  UPDATE_COUNTRY,
  COMPLETED_TEST_SESSIONS_BY_USER,
  GET_TESTER_DATA,
} from './graphql';
import { HIDE_TESTER } from '../../../core/graphql/tester';
import { DELETE_USER, UPDATE_USER_STATUS } from '../../../core/graphql/user';
import { UPDATE_TESTER_PAYMENT_STATUS } from '../../../core/graphql/tester-payment';
import ChangeStatusDialog from '../../components/change-status-dialog';
import CompletedTestsSegment from '../../../core/components/completed-tests-segment';
import withDashboardSidebar from '../../components/with-dashboard-sidebar';
import WriteFeedbackModal from './write-feedback-modal';
import { PAYMENT_STATUSES } from '../../../core/constants/payments';
import { countryOptions } from '../../../tester/pages/sign-up-survey/options';
import UserTrackingTable from '../../components/user-tracking-table';

import {
  ButtonAddPayment,
  ButtonBack,
  ButtonDeleteUser,
  Container,
  ContainerName,
  ContainerTable,
  ContainerTesterStatus,
  LinkChangeTesterStatus,
  Name,
  TextTesterStatus,
  TopContainer,
  Radio,
  ProfileEditionContainer,
  TextProfileEditing,
  Dropdown,
  ButtonTab,
  ContainerButtonTab,
  AdditionalInformation,
  Text,
} from './styles';
import { UPDATE_IS_BANNED_MODERATED_AT } from '../manage-testers/is-banned-moderated-component/graphql';

import Referrals from './referrals';
import PastStudies from './past-studies';

const Tester = (props) => {
  const {
    history: { push },
    match: { params: { id } },
  } = props;

  /** mutations */
  const [hideTester] = useMutation(HIDE_TESTER);
  const [deleteUser] = useMutation(DELETE_USER);
  const [updatePaymentStatus, { loading: changePaymentStatusLoading }] = useMutation(UPDATE_TESTER_PAYMENT_STATUS);
  const [writeFeedback] = useMutation(WRITE_FEEDBACK);
  const [resetRatingAndReview] = useMutation(RESET_RATING_AND_REVIEW);
  const [updateStatus, { loading: changeStatusLoading }] = useMutation(UPDATE_USER_STATUS);
  const [lockProfileEdition, { loading: lockToggleLoading }] = useMutation(LOCK_PROFILE_EDITION);
  const [unlockProfileEdition, { loading: unlockToggleLoading }] = useMutation(UNLOCK_PROFILE_EDITION);
  const [updateCountry] = useMutation(UPDATE_COUNTRY);

  /** states */
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [testSession, setTestSession] = useState({});
  const [showChangeStatusModal, setShowChangeStatusModal] = useState(false);
  const [changeStatusError, setChangeStatusError] = useState(null);
  const [changePaymentStatusError, setChangePaymentStatusError] = useState(null);
  const [editionToggleError, setEditionToggleError] = useState(null);
  const [testerPayment, setTesterPayment] = useState({});
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectTab, setSelectTab] = useState('Completed Tests');

  const [updateUserBanModerated, { errorMutationModerated }] = useMutation(UPDATE_IS_BANNED_MODERATED_AT);

  const {
    loading: userDataLoading,
    data: userData = {},
    error: userDataError,
  } = useQuery(GET_TESTER_DATA, {
    variables: {
      userId: id,
    },
    skip: !id,
  }, {
    dataField: 'users',
  });
  const { user: userInformation } = userData;
  const {
    data = {},
    refetch: userRefetch,
  } = useQuery(COMPLETED_TEST_SESSIONS_BY_USER, {
    variables: {
      userId: id,
      isCompleted: true,
      order: 'created_at DESC',
      showRemoved: true,
    },
  });

  const {
    data: editionData = {},
    loading: editionLoading,
    error: editionError,
    networkStatus: editionNetworkStatus,
    refetch: editionRefetch,
  } = useQuery(LAST_EDITION, {
    variables: {
      userId: id,
    },
    notifyOnNetworkStatusChange: true,
  });

  const {
    testSessions = [],
    user = {},
  } = data;

  const {
    user: { profile: { editionLocked } = {} } = {},
  } = editionData;

  const canEdit = useMemo(() => !editionLocked, [editionLoading]);

  const handleClickWriteFeedback = (session) => {
    setTestSession(session);
  };

  const handleClickResetRatingAndReview = async (testSessionId) => {
    try {
      await resetRatingAndReview({
        variables: {
          testSessionId,
        },
      });
      setTestSession({});
    } catch (e) {
      captureException('RESET_RATING_ERROR', e);
    }
  };

  const handleSaveFeedback = async (testSessionId, newFeedback) => {
    try {
      await writeFeedback({
        variables: {
          testSessionId,
          feedback: newFeedback,
        },
      });
      setTestSession({});
    } catch (e) {
      captureException('WRITE_FEEDBACK_FAILURE', e);
    }
  };

  const handleHideTester = async (testerId) => {
    try {
      await hideTester({
        variables: {
          testerId,
        },
      });
    } catch (e) {
      captureException('HIDE_TESTER_FAILURE', e);
    }
  };

  const handleDeleteUser = async () => {
    try {
      await deleteUser({ variables: { userId: id } });
      push('/testers');
    } catch (e) {
      captureException('DELETE_USER_FAILURE', e);
    }
  };

  const handleCloseChangeStatusModal = () => {
    setShowChangeStatusModal(false);
  };

  const handleSaveStatus = async (newStatus) => {
    if (newStatus !== user.status) {
      try {
        await updateStatus({
          variables: {
            userId: id,
            status: newStatus,
          },
        });

        handleCloseChangeStatusModal();
      } catch (error) {
        setChangeStatusError(error);
        captureException('UPDATE_USER_STATUS_FAILURE', error);
      }
    }
  };

  const handleSaveTesterPayment = async (newStatus) => {
    if (newStatus !== user.status) {
      try {
        await updatePaymentStatus({
          variables: {
            testerPaymentId: testerPayment.id,
            status: newStatus,
          },
        });

        setTesterPayment({});
      } catch (error) {
        setChangePaymentStatusError(error);
        captureException('UPDATE_PAYMENT_STATUS_FAILURE', error);
      }
    }
  };

  const editMutation = async (mutation) => {
    try {
      await mutation({
        variables: {
          userId: id,
        },
      });
      editionRefetch();
      setEditionToggleError(null);
    } catch (error) {
      setEditionToggleError(error);
    }
  };

  const handleEditionToggle = async () => {
    if (!lockToggleLoading && !unlockToggleLoading) {
      if (canEdit) {
        editMutation(lockProfileEdition);
      } else {
        editMutation(unlockProfileEdition);
      }
    }
  };

  const handleClickCountry = async (e, { value }) => {
    try {
      await updateCountry({
        variables: {
          userId: id,
          country: value,
        },
      });
      setSelectedCountry(value);
    } catch (error) {
      captureException('UPDATE_COUNTRY_FAILURE', error);
    }
  };

  const name = useMemo(() => (!_.isEmpty(user) && `${user.profile.firstName} ${user.profile.lastName}`), [user]);

  const country = useMemo(() => (!_.isEmpty(user?.profile?.country) && `${user.profile.country}`), [user]);

  const onChangeToggle = async (event) => {
    const isChecked = event.target.checked;

    const params = {
      variables: {
        userId: user.id,
        isBannedModeratedAt: !isChecked,
      },
    };
    await updateUserBanModerated(params);
    userRefetch();
  };

  return (
    <>
      <WriteFeedbackModal
        open={!_.isEmpty(testSession)}
        onClose={() => setTestSession({})}
        onSubmit={handleSaveFeedback}
        testSession={testSession}
      />

      <ConfirmDialog
        show={showDeleteModal}
        title="Delete?"
        warning="Warning: This cannot be undone"
        confirmText="Yes, delete"
        cancelText="Cancel"
        handleConfirm={handleDeleteUser}
        handleClose={() => setShowDeleteModal(false)}
      />

      <ChangeStatusDialog
        open={showChangeStatusModal}
        status={!_.isEmpty(user) && user.status}
        onClose={handleCloseChangeStatusModal}
        onClickSave={handleSaveStatus}
        loading={changeStatusLoading}
        error={changeStatusError}
        statuses={[
          { value: 'pending_approval', text: 'Pending Approval' },
          { value: 'active', text: 'Active' },
        ]}
      />

      <ChangeStatusDialog
        open={!_.isEmpty(testerPayment)}
        status={!_.isEmpty(testerPayment) && testerPayment.status}
        onClose={() => setTesterPayment({})}
        onClickSave={handleSaveTesterPayment}
        loading={changePaymentStatusLoading}
        error={changePaymentStatusError}
        statuses={PAYMENT_STATUSES}
      />

      <Container>
        <ButtonBack
          onClick={() => push('/manage-testers')}
        >
          &#60; Back To participants
        </ButtonBack>
        <ButtonAddPayment>
          Add Payment
        </ButtonAddPayment>
        <TopContainer>
          <ContainerName>
            <Name>{name}</Name>
            <Dropdown
              name="country"
              placeholder="Select"
              options={countryOptions}
              search
              scrolling
              fluid
              selection
              onChange={handleClickCountry}
              value={selectedCountry || country || null}
            />
          </ContainerName>
          <ContainerTesterStatus>
            {user.profile && (
              <ProfileEditionContainer>
                <TextProfileEditing>
                  Profile editing
                </TextProfileEditing>
                {(editionLoading || editionNetworkStatus === editionNetworkStatus.refetch || lockToggleLoading || unlockToggleLoading) && (
                  <Loader data-loader="profile-editing-loader" />
                )}
                {(editionError || editionToggleError) && (
                  <ErrorHandler
                    error={editionError || editionToggleError}
                    defaultMessage="Error loading profile editing"
                  />
                )}
                {!editionLoading && !editionError && !lockToggleLoading && !unlockToggleLoading && (
                  <Radio
                    toggle
                    checked={canEdit}
                    onClick={handleEditionToggle}
                  />
                )}
              </ProfileEditionContainer>
            )}
            <TextTesterStatus>
              {`Tester Status: ${!_.isEmpty(user) && _.startCase(user.status)}`}
              <LinkChangeTesterStatus
                onClick={() => setShowChangeStatusModal(true)}
              >
                change
              </LinkChangeTesterStatus>
            </TextTesterStatus>
            <ProfileEditionContainer>
              <TextProfileEditing>
                Moderated
              </TextProfileEditing>
              <Toggle
                checked={!(user.isBannedModeratedAt)}
                onChange={onChangeToggle}
                id={`toggle-${user?.id}`}
              />
              {errorMutationModerated && (
                <ErrorHandler
                  error={errorMutationModerated}
                  defaultMessage="Error to update moderated ban to this user."
                />
              )}
            </ProfileEditionContainer>
          </ContainerTesterStatus>
          <ButtonDeleteUser onClick={() => setShowDeleteModal(true)}>
            Delete User
          </ButtonDeleteUser>
        </TopContainer>
        <AdditionalInformation>
          <Text>
            {`Email address: ${userInformation?.email || ''}`}
          </Text>
          <Text>
            {`Paypal address: ${userInformation?.profile?.paypalAccount || ''}`}
          </Text>
        </AdditionalInformation>
        <ContainerButtonTab>
          <ButtonTab
            style={{
              backgroundColor: selectTab === 'Completed Tests' ? '#dce7fa' : 'white',
            }}
            onClick={() => setSelectTab('Completed Tests')}
          >
            Completed Tests
          </ButtonTab>
          <ButtonTab
            style={{
              backgroundColor: selectTab === 'Locations' ? '#dce7fa' : 'white',
            }}
            onClick={() => setSelectTab('Locations')}
          >
            Locations
          </ButtonTab>
          {
            userDataLoading
              ? <Loader data-loader="user-studies-loader" />
              : (
                <>
                  <ButtonTab
                    style={{
                      backgroundColor: selectTab === 'Past Studies' ? '#dce7fa' : 'white',
                    }}
                    onClick={() => setSelectTab('Past Studies')}
                  >
                    Past Studies
                  </ButtonTab>
                  <ButtonTab
                    style={{
                      backgroundColor: selectTab === 'Referrals' ? '#dce7fa' : 'white',
                    }}
                    onClick={() => setSelectTab('Referrals')}
                  >
                    Referrals
                  </ButtonTab>
                </>
              )
          }
          {
            userDataError && (
              <ErrorHandler
                error={userDataError}
                defaultMessage="Error loading Screener/Past Studies/Referrals"
              />
            )
          }
        </ContainerButtonTab>
        <ContainerTable>
          {
            selectTab === 'Completed Tests' && (
              <CompletedTestsSegment
                onHide={handleHideTester}
                onClickWritefeedback={handleClickWriteFeedback}
                onClickResetRatingAndReview={handleClickResetRatingAndReview}
                testSessions={testSessions}
                onChangePaymentStatus={setTesterPayment}
                isAdmin
              />
            )
          }
          {
            selectTab === 'Locations' && (
              <UserTrackingTable userId={id} />
            )
          }

          {
            selectTab === 'Past Studies' && (
              <PastStudies pastStudies={userInformation.testers} />
            )
          }

          {
            selectTab === 'Referrals' && (
              <Referrals referrals={userInformation.referrals} />
            )
          }
        </ContainerTable>

      </Container>
    </>
  );
};

Tester.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
};

export default withDashboardSidebar(Tester);
